import parse from 'html-react-parser'
import _ from 'lodash'
import moment from 'moment'
import React, { useEffect } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'redux'
import { Field, FieldArray, InjectedFormProps, change, getFormValues, reduxForm } from 'redux-form'

import { noop } from 'constants/global'
import { Option } from 'constants/models'
import {
  AllergyTypes,
  Child,
  FAMILY_MARITAL_STATUS_OPTIONS,
  FAMILY_POSITIONS_OPTIONS,
  GENDER_OPTIONS,
  LANGUAGES_OPTIONS,
} from 'services/child/constants'
import { getFormBuilderStructure } from 'services/formBuilderSettings/constants'
import {
  FormBuilderField,
  FormBuilderGroup,
  FormBuilderSection,
  FormBuilderSubGroup,
} from 'services/formBuilderSettings/models'
import {
  ALLERGY_OPTIONS,
  DIETARY_NO_PREFERENCE,
  DIETARY_OTHER,
  DIETARY_REQUIREMENTS_OPTIONS,
} from 'services/legacy/child/constants'
import { NurserySettings, NurserySettingsFormBuilder } from 'services/nurseries/models'
import {
  FormPreviewCarerChildRelation,
  FormPreviewFormValues,
  RegistrationForm,
  RegistrationFormObjectAction,
  RegistrationFormStatus,
} from 'services/registrationForms/models'
import { User } from 'services/users/models'

import { isRequired, isValidPhotoUploadType } from 'utils/fieldValidation'

import appSelectors from 'services/app/selectors'
import formBuilderSettingsSelectors from 'services/formBuilderSettings/selectors'
import { ethnicitiesSelectors, withEthnicitiesService, withEthnicitiesServiceProps } from 'services/legacy/ethnicities'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'

import {
  Button,
  DateString,
  Flex,
  Form,
  Hr,
  LastSaveDateTime,
  Space,
  StickyFooter,
  Tooltip,
  Typography,
} from 'components'
import { ModalType } from 'modals'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import BookingPattern from '../BookingPattern'
import { StyledRow, StyledWrapper } from './FormPreviewStyled'

export const FORM_PREVIEW_FORM = 'formPreview'

const mapState = (state: RootState) => ({
  drinkingMethodsDropdownOptions: appSelectors.getDrinkingMethodsDropdownOptions(),
  ethnicitiesOptions: ethnicitiesSelectors.getEthnicitiesListDataDropdownSelector(state),
  formValues: getFormValues(FORM_PREVIEW_FORM)(state) as FormPreviewFormValues,
  toiletTrainingDropdownOptions: appSelectors.getToiletTrainingDropdownOptions(),
  userTitles: appSelectors.getUserTitlesOptions(),
})

const mapDispatch = {
  changeField: (field, value) => change(FORM_PREVIEW_FORM, field, value),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

interface FormPreviewProps {
  child?: Child
  isFinanceV3Enabled?: boolean
  isPreview?: boolean
  isSubmitting?: boolean
  isSubmittingAsCompleted?: boolean
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  nurserySettings?: NurserySettings
  onSubmit: (values: FormPreviewFormValues, status: RegistrationFormStatus) => void
  registrationForm?: RegistrationForm
}

type FormPreviewFullProps = FormPreviewProps
  & InjectedFormProps<FormPreviewFormValues, FormPreviewProps>
  & PropsFromRedux
  & withEthnicitiesServiceProps
  & withModalServiceProps
  & withRouterProps

const renderParentForm = ({
  fields,
  formValues,
  group,
  isSubmitting,
  nurseryFormBuilderSettings,
  onMarkAsRemoved,
  registrationForm,
  renderGroup,
}: {
  fields: any
  formValues: FormPreviewFormValues
  group: FormBuilderGroup
  isSubmitting?: boolean
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  onMarkAsRemoved: (index: number) => void
  registrationForm: RegistrationForm
  renderGroup: (group: FormBuilderGroup, disableHr?: boolean, indexOfArray?: number) => React.ReactNode
}) => {
  const finalNewCarers = _.filter(formValues?.data?.carerChildRelations, (carerChildRelation) => (
    !(carerChildRelation?.objectId)
  ))?.length || 0

  const handleAddNewCarer = () => {
    if (isSubmitting) {
      return
    }

    if (finalNewCarers >= nurseryFormBuilderSettings.maxParents) {
      return
    }

    fields.push({})
  }

  return (
    <React.Fragment key={group.key}>
      {_.map(fields, (field, index) => {
        if (RegistrationFormObjectAction.DELETE === formValues?.data?.carerChildRelations?.[index].objectAction) {
          return null
        }

        const filteredCarers = _.filter(
          _.map(formValues?.data?.carerChildRelations, (item: FormPreviewCarerChildRelation, i) => ({
            ...item,
            index: i,
          })),
          (item: FormPreviewCarerChildRelation) => (
            RegistrationFormObjectAction.DELETE !== item.objectAction
          ),
        )

        const finalIndex = _.findIndex(filteredCarers, (item) => item.index === index)

        const value = formValues?.data?.carerChildRelations?.[index]
        const isMainCarer = value?.carer?.objectId
          && registrationForm?.carer?.id
          && value?.carer?.objectId === registrationForm?.carer?.id

        const showFormInputs = isMainCarer || !(value?.objectId)

        return (
          <div key={`${group.key}-${index}`}>
            <Flex alignItems="center" gap="10px" height="50px">
              <Flex flex="1">
                <Typography fontSize={18} bold>
                  {i18n.t('module:RegistrationForm:parentGuardian')}
                  {' '}
                  {finalIndex + 1}
                </Typography>
              </Flex>
              {showFormInputs && !isMainCarer && (
                <Flex>
                  <Button
                    disabled={isMainCarer}
                    hierarchy="tertiary"
                    icon="trash"
                    size="small"
                    onClick={() => !isMainCarer && onMarkAsRemoved(index)}
                  />
                </Flex>
              )}
            </Flex>
            {showFormInputs
              ? renderGroup(group, index === fields.length - 1, index)
              : (
                <React.Fragment>
                  <Typography>
                    {`${value?.carer?.firstName} ${value?.carer?.surname}`}
                  </Typography>
                  <Typography>
                    {value?.relation?.label || '-'}
                  </Typography>
                  {index !== fields.length - 1 && <Hr margin="32px 0" />}
                </React.Fragment>
              )}
          </div>
        )
      })}
      <React.Fragment>
        <Space space="32px" />
        <Button
          disabled={isSubmitting || finalNewCarers >= nurseryFormBuilderSettings.maxParents}
          label={i18n.t('module:RegistrationForm:addAnotherParentGuardian')}
          onClick={handleAddNewCarer}
        />
      </React.Fragment>
      <Hr margin="32px 0" />
    </React.Fragment>
  )
}

const FormPreview: React.FC<FormPreviewFullProps> = ({
  changeField,
  child,
  drinkingMethodsDropdownOptions,
  ethnicitiesActions,
  ethnicitiesOptions,
  formValues,
  handleSubmit,
  isFinanceV3Enabled,
  isPreview,
  isSubmitting,
  isSubmittingAsCompleted,
  modalActions,
  nurseryFormBuilderSettings,
  nurserySettings,
  onSubmit,
  registrationForm,
  toiletTrainingDropdownOptions,
  userTitles,
}) => {
  const formBuilderStructure = formBuilderSettingsSelectors.getAvailableFields(
    getFormBuilderStructure(i18n),
    nurseryFormBuilderSettings,
  )

  useEffect(() => {
    ethnicitiesActions.list()

    return () => {
      ethnicitiesActions.clear()
    }
  }, [])

  const handleChangeAllergies = () => {
    changeField('data.information.allergiesDetails', null)
  }

  const handleChangeDaytimeSleepingBehavior = () => {
    changeField('data.information.daytimeSleepingBehaviorDetails', null)
  }

  const handleChangeEnglishAsAdditionalLanguage = () => {
    changeField('data.profile.englishAsAdditionalLanguageDetails', null)
  }

  const handleChangeSpecialEducationalNeeds = () => {
    changeField('data.profile.specialEducationalNeedsDetails', null)
  }

  const handleChangeDietaryReq = (option: Option) => {
    if (option.value === DIETARY_NO_PREFERENCE) {
      changeField('data.information.dietaryReqDesc', null)
    }
  }

  const renderFieldName = (name: string, indexOfArray?: number) => {
    const finalName = name.replace('fields.child.', 'data.')

    if (_.isNumber(indexOfArray)) {
      return finalName.replace('carerChildRelations', `carerChildRelations[${indexOfArray}]`)
    }

    return finalName
  }

  const renderRow = (children: React.ReactNode, field: FormBuilderField, options?: { required?: boolean }) => (
    <Form.Row
      label={field.label}
      margin="0"
      required={_.isBoolean(options?.required)
        ? options.required
        : (field?.defaultDisabled?.registration || field.required)}
      width={{ field: '100%' }}
      verticalLabel
    >
      {children}
    </Form.Row>
  )

  const renderField = (field: FormBuilderField, indexOfArray?: number) => {
    switch (field.key) {
      case 'fields.child.photo':
      case 'fields.child.carerChildRelations.carer.photo': {
        return renderRow(
          <Field
            component={Form.PhotoUpload}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            validate={isValidPhotoUploadType}
          />,
          field,
        )
      }
      case 'fields.child.gender': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={GENDER_OPTIONS}
            placeholder={field.placeholder}
            validate={isRequired}
            buttonStyle
            horizontal
          />,
          field,
        )
      }
      case 'fields.child.ethnicity': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={ethnicitiesOptions}
            placeholder={field.placeholder}
          />,
          field,
        )
      }
      case 'fields.child.information.dietaryReqDesc': {
        const value = formValues?.data?.information?.dietaryReq?.value
          || formValues?.data?.information?.dietaryReq

        if (value === DIETARY_NO_PREFERENCE) {
          return null
        }

        return renderRow(
          <Field
            component={Form.TextAreaField}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.label}
            validate={DIETARY_OTHER === value && isRequired}
          />,
          field,
          { required: DIETARY_OTHER === value },
        )
      }
      case 'fields.child.profile.familyPosition': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={FAMILY_POSITIONS_OPTIONS}
            placeholder={field.label}
          />,
          field,
        )
      }
      case 'fields.child.religion': {
        return renderRow(
          <Field
            component={Form.InfiniteDropdowns.SupportedReligions}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.placeholder}
            clearable
          />,
          field,
        )
      }
      case 'fields.child.profile.specialEducationalNeeds': {
        return (
          <Flex flexDirection="column" gap="10px">
            <Field
              component={Form.Checkbox}
              disabled={isSubmitting}
              label={field.label}
              name={renderFieldName(field.key, indexOfArray)}
              onChange={handleChangeSpecialEducationalNeeds}
            />
            {formValues?.data?.profile?.specialEducationalNeeds && (
              <Field
                component={Form.TextField}
                disabled={isSubmitting}
                name="data.profile.specialEducationalNeedsDetails"
                placeholder={field.placeholder}
              />
            )}
          </Flex>
        )
      }
      case 'fields.child.profile.englishAsAdditionalLanguage': {
        return (
          <Flex flexDirection="column" gap="10px">
            <Field
              component={Form.Checkbox}
              disabled={isSubmitting}
              label={field.label}
              name={renderFieldName(field.key, indexOfArray)}
              onChange={handleChangeEnglishAsAdditionalLanguage}
            />
            {formValues?.data?.profile?.englishAsAdditionalLanguage && (
              <Field
                component={Form.TextField}
                disabled={isSubmitting}
                name="data.profile.englishAsAdditionalLanguageDetails"
                placeholder={field.placeholder}
              />
            )}
          </Flex>
        )
      }
      case 'fields.child.information.daytimeSleepingBehavior': {
        return (
          <Flex flexDirection="column" gap="10px">
            <Field
              component={Form.Checkbox}
              disabled={isSubmitting}
              label={field.label}
              name={renderFieldName(field.key, indexOfArray)}
              onChange={handleChangeDaytimeSleepingBehavior}
            />
            {formValues?.data?.information?.daytimeSleepingBehavior && (
              <Field
                component={Form.TextField}
                disabled={isSubmitting}
                name="data.information.daytimeSleepingBehaviorDetails"
                placeholder={field.placeholder}
              />
            )}
          </Flex>
        )
      }
      case 'fields.child.profile.familyMaritalStatus': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={FAMILY_MARITAL_STATUS_OPTIONS}
            placeholder={field.placeholder}
          />,
          field,
        )
      }
      case 'fields.child.profile.familySpokenLanguage': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={LANGUAGES_OPTIONS}
            placeholder={field.label}
            multi
          />,
          field,
        )
      }
      case 'fields.child.birthDate': {
        return renderRow(
          <Field component={Form.DatePicker} disabled={isSubmitting} name={renderFieldName(field.key, indexOfArray)} />,
          field,
        )
      }
      case 'fields.child.information.allergies': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={ALLERGY_OPTIONS}
            placeholder={field.label}
            onChange={handleChangeAllergies}
          />,
          field,
        )
      }
      case 'includeBookingPattern': {
        return (
          <BookingPattern
            child={child}
            isFinanceV3Enabled={isFinanceV3Enabled}
            isPreview={isPreview}
            nurserySettings={nurserySettings}
          />
        )
      }
      case 'enrollmentDetails': {
        return (
          <StyledWrapper>
            <StyledRow>
              <Form.Row
                label={_.upperFirst(i18n.t('global:startDate'))}
                margin="0"
                width={{ field: '100%' }}
                verticalLabel
              >
                {child?.startDate ? <DateString date={child.startDate} /> : '-'}
              </Form.Row>
            </StyledRow>
            <StyledRow>
              <Form.Row label={i18n.t('global:LeavingDate')} margin="0" width={{ field: '100%' }} verticalLabel>
                {child?.leavingDate ? <DateString date={child.leavingDate} /> : '-'}
              </Form.Row>
            </StyledRow>
            <StyledRow>
              <Form.Row
                label={i18n.t('module:RegistrationForm:roomToStart')}
                margin="0"
                width={{ field: '100%' }}
                verticalLabel
              >
                {child?.nurseryClass?.name ?? '-'}
              </Form.Row>
            </StyledRow>
            <StyledRow>
              <Form.Row label={i18n.t('global:KeyPerson')} margin="0" width={{ field: '100%' }} verticalLabel>
                {child?.keyWorkers?.length
                  ? _.map(child.keyWorkers, (keyPerson: User, index: number) => (
                    <Typography key={keyPerson?.id} lineHeight={22}>
                      {keyPerson?.fullName}
                      {child.keyWorkers.length - 1 !== index && (
                        <React.Fragment>
                          ,
                          <br />
                        </React.Fragment>
                      )}
                    </Typography>
                  ))
                  : '-'}
              </Form.Row>
            </StyledRow>
          </StyledWrapper>
        )
      }
      case 'fields.child.information.allergiesDetails': {
        if (
          (formValues?.data?.information?.allergies as Option<AllergyTypes>)?.value !== AllergyTypes.HAS_ALLERGY
          && formValues?.data?.information?.allergies !== AllergyTypes.HAS_ALLERGY
        ) {
          return null
        }

        return renderRow(
          <Field
            component={Form.TextAreaField}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.label}
            validate={isRequired}
          />,
          field,
          { required: true },
        )
      }
      case 'fields.child.information.dietaryReq': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={DIETARY_REQUIREMENTS_OPTIONS}
            placeholder={field.placeholder}
            onChange={(value: any) => { handleChangeDietaryReq(value) }}
          />,
          field,
        )
      }
      case 'fields.child.information.toiletTrained': {
        return renderRow(
          <React.Fragment>
            <Field
              component={Form.RadioGroup}
              disabled={isSubmitting}
              name={renderFieldName(field.key, indexOfArray)}
              options={toiletTrainingDropdownOptions}
            />
            <Space space="10px" />
          </React.Fragment>,
          field,
        )
      }
      case 'fields.child.information.drinkingMethod': {
        return renderRow(
          <React.Fragment>
            <Field
              component={Form.RadioGroup}
              disabled={isSubmitting}
              name={renderFieldName(field.key, indexOfArray)}
              options={drinkingMethodsDropdownOptions}
            />
            <Space space="10px" />
          </React.Fragment>,
          field,
        )
      }
      case 'fields.child.carerChildRelations.carer.workTelephone':
      case 'fields.child.carerChildRelations.carer.telephone':
      case 'fields.child.carerChildRelations.carer.mobile': {
        return renderRow(
          <Field
            component={Form.TextField}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.label}
            type="tel"
          />,
          field,
        )
      }
      case 'fields.child.carerChildRelations.carer.email': {
        const value = formValues?.data?.carerChildRelations[indexOfArray]
        const isMainCarer = value?.carer?.objectId
          && registrationForm?.carer?.id
          && value?.carer?.objectId === registrationForm?.carer?.id

        const row = renderRow(
          <Field
            component={Form.TextField}
            disabled={isMainCarer || isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.label}
            type="email"
            validate={isRequired}
          />,
          field,
        )

        if (isMainCarer) {
          return (
            <Tooltip placement="bottom" title={i18n.t('module:RegistrationForm:mainCarerEmail')}>
              {row}
            </Tooltip>
          )
        }

        return row
      }
      case 'fields.child.carerChildRelations.relation': {
        return renderRow(
          <Field
            component={Form.InfiniteDropdowns.Relations}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.placeholder}
            creatable
            deviceTokenNotRequired
          />,
          field,
        )
      }
      case 'agreement': {
        return (
          <Typography>
            {nurseryFormBuilderSettings?.agreement && parse(nurseryFormBuilderSettings.agreement)}
          </Typography>
        )
      }
      case 'fields.child.carerChildRelations.carer.title': {
        return renderRow(
          <Field
            component={Form.Select}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            options={userTitles}
            placeholder={field.label}
          />,
          field,
        )
      }
      case 'fields.child.profile.languageCapability':
      case 'fields.child.profile.movementCapability':
      case 'fields.child.profile.medicalInformation':
      case 'fields.child.profile.childBehavior':
      case 'fields.child.profile.routine':
      case 'fields.child.profile.attendsOtherSetting':
      case 'fields.child.profile.toysPreference':
      case 'fields.child.profile.booksPreference':
      case 'fields.child.profile.foodsPreference':
      case 'fields.child.profile.otherPreference':
      case 'fields.child.information.address':
      case 'fields.child.carerChildRelations.carer.address': {
        return renderRow(
          <Field
            component={Form.TextAreaField}
            disabled={isSubmitting}
            height={100}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.placeholder || field.label}
          />,
          field,
        )
      }
      case 'fields.child.information.hasPermissionToBeTakenToHospital':
      case 'fields.child.information.canConsumeFoodWithNutTraces':
      case 'fields.child.information.canReceiveMedicine':
      case 'fields.child.information.useOnSiteMediaPermitted':
      case 'fields.child.information.closedSocialMediaPhotoSharingPermitted':
      case 'fields.child.profile.parentsAreServingArmedForcesPersonnel':
      case 'fields.child.information.applyPermissions.patches':
      case 'fields.child.information.applyPermissions.sunCream':
      case 'fields.child.information.applyPermissions.nappyCream':
      case 'fields.child.information.applyPermissions.facePaint':
      case 'fields.child.information.canBePhotographed':
      case 'fields.child.information.canBeTaggedInGroupPhotos':
      case 'fields.child.information.socialMediaPhotoSharingPermitted':
      case 'fields.child.information.otherMarketingMaterialPhotoSharingPermitted':
      case 'fields.child.profile.isFussyEater':
      case 'fields.child.information.travelPermissions.canGoToExternalOutgoings':
      case 'fields.child.information.travelPermissions.canTakeTheBus': {
        return (
          <Field
            component={Form.Checkbox}
            disabled={isSubmitting}
            label={field.label}
            name={renderFieldName(field.key, indexOfArray)}
          />
        )
      }
      default: {
        return renderRow(
          <Field
            component={Form.TextField}
            disabled={isSubmitting}
            name={renderFieldName(field.key, indexOfArray)}
            placeholder={field.placeholder || field.label}
            validate={
              field?.defaultDisabled?.registration || field?.required ? [isRequired] : []
            }
          />,
          field,
        )
      }
    }
  }

  const handleMarkAsRemoved = (index: number) => {
    modalActions.show<ModalType.CONFIRM>(ModalType.CONFIRM, {
      cancelButtonLabel: i18n.t('global:Cancel'),
      confirmButtonLabel: i18n.t('global:Delete'),
      icon: 'warning',
      onConfirm: () => changeField(`data.carerChildRelations[${index}].objectAction`, 'delete'),
      text: i18n.t('module:RegistrationForm:removeCarer'),
    })
  }

  const renderSubGroup = (
    subGroup: FormBuilderSubGroup,
    indexOfArray?: number,
    section?: FormBuilderSection,
  ) => (subGroup.hideInForm ? null : (
    <div key={subGroup.key}>
      <Typography margin={section?.hideInForm ? '0 0 32px' : '32px 0 16px'} variant={section?.hideInForm ? 'h5' : 'h6'}>
        {subGroup.label}
      </Typography>
      <StyledWrapper>
        {_.map(subGroup.fields, (field: FormBuilderField) => (
          <StyledRow $doubleColumn={field.doubleColumn} key={field.key}>
            {renderField(field, indexOfArray)}
          </StyledRow>
        ))}
      </StyledWrapper>
      {section?.hideInForm && <Hr margin="32px 0" />}
    </div>
  ))

  const renderGroup = (
    group: FormBuilderGroup,
    disableHr?: boolean,
    indexOfArray?: number,
    section?: FormBuilderSection,
  ) => (
    <div key={group.key}>
      <Typography variant="h5">{group.label}</Typography>
      {_.map(group.subGroups, (subGroup: FormBuilderSubGroup) => renderSubGroup(subGroup, indexOfArray, section))}
      {(!disableHr && !section?.hideInForm) && <Hr margin="32px 0" />}
    </div>
  )

  const renderParentGroup = (group: FormBuilderGroup) => (
    <FieldArray
      component={renderParentForm}
      key={group.key}
      name="data.carerChildRelations"
      props={{
        formValues,
        group,
        isSubmitting,
        nurseryFormBuilderSettings,
        onMarkAsRemoved: handleMarkAsRemoved,
        registrationForm,
        renderGroup,
      }}
    />
  )

  const handleShowTermsAndConditionsModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault()

    if (nurseryFormBuilderSettings.isTermsAndConditionsURL) {
      window.open(nurseryFormBuilderSettings.termsAndConditions, '_blank', 'noopener, noreferrer')

      return
    }

    modalActions.show<ModalType.TERMS_AND_CONDITIONS>(ModalType.TERMS_AND_CONDITIONS, {
      content: nurseryFormBuilderSettings.termsAndConditions,
    })
  }

  const renderSection = (section: FormBuilderSection) => {
    if ('termsAndConditions' === section.key) {
      return (
        <div key={section.key}>
          <Typography margin="0 0 32px" variant="h5">
            {i18n.t('module:RegistrationForm:signature')}
          </Typography>
          <Form.Row
            label={i18n.t('module:RegistrationForm:drawYourSignature')}
            width={{ field: '100%' }}
            required
            verticalLabel
          >
            <Field component={Form.SignaturePad} disabled={isSubmitting} name="signature" validate={isRequired} />
          </Form.Row>
          <Field
            component={Form.Checkbox}
            disabled={isSubmitting}
            label={(
              <Typography userSelect="none">
                {i18n.t('module:RegistrationForm:termsAndConditions', {
                  components: [(
                    <Typography
                      id="data-terms"
                      variant="span"
                      bold
                      primary
                      underline
                      onClick={handleShowTermsAndConditionsModal as any}
                    />
                  )],
                  contactName: registrationForm?.carer?.name,
                })}
              </Typography>
            )}
            name="terms"
            validate={isRequired}
          />
        </div>
      )
    }

    return (
      <div key={section.key}>
        {'bookingPattern' === section.key && (
          <Typography margin="0 0 25px">{i18n.t('module:RegistrationForm:bookingPatternInfo')}</Typography>
        )}
        {!section.hideInForm && (
          <Typography margin="0 0 32px" variant="h5">{section.label}</Typography>
        )}
        {_.map(section.groups, (group: FormBuilderGroup) => {
          if ('parentInformation' === group.key) {
            return renderParentGroup(group)
          }

          return renderGroup(group, false, null, section)
        })}
      </div>
    )
  }

  return (
    <Form onSubmit={noop}>
      {_.map(formBuilderStructure, renderSection)}
      {!isPreview && (
        <React.Fragment>
          <Space space="80px" />
          <StickyFooter>
            <Button
              disabled={isSubmittingAsCompleted}
              hierarchy="tertiary"
              isLoading={isSubmitting && !isSubmittingAsCompleted}
              label={i18n.t('global:saveAsDraft')}
              onClick={() => onSubmit(formValues, RegistrationFormStatus.SENT)}
            />
            <LastSaveDateTime lastSaveDateTime={registrationForm && moment(registrationForm.updatedAt)} />
            <StickyFooter.Flex />
            <Button
              disabled={isSubmitting && !isSubmittingAsCompleted}
              isLoading={isSubmittingAsCompleted}
              label={i18n.t('global:Submit')}
              onClick={() => handleSubmit((values) => onSubmit(values, RegistrationFormStatus.COMPLETED))()}
            />
          </StickyFooter>
        </React.Fragment>
      )}
    </Form>
  )
}

const enhance = compose(
  withEthnicitiesService,
  withModalService,
  withRouter,
  connector,
)

export default reduxForm<FormPreviewFormValues, FormPreviewProps>({ form: FORM_PREVIEW_FORM })(enhance(FormPreview))
